<script setup lang="ts">
import { ref, watch } from 'vue'
import { useGaleriaStore } from '@/pages/admin/galeria/store/useGaleriaStore'

const store = useGaleriaStore()
const previews = ref<{ id: string | null; file: File | undefined; url: string }[]>([])
const loading = ref<boolean[]>([])

definePage({
  meta: {
    action: 'create',
    subject: 'galeria',
  },
})

const {
  fetchGaleria,
  uploadToServer,
  deleteGaleria,
} = store

const {
  galeria,
} = storeToRefs(store)

const removing = ref<boolean[]>([])

// Atualiza previews quando galeria muda
watch(
  galeria,
  value => {
    if (!value || !value.length) {
      previews.value = []
      loading.value = []

      return
    }
    previews.value = value.map(item => ({
      id: item.id, // ajuste conforme o campo correto do IGaleria
      file: undefined,
      url: item.url, // ajuste conforme o campo correto do IGaleria
    }))
    loading.value = Array(previews.value.length).fill(false)
  },
  { immediate: true },
)

const handleFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files)
    return
  handleFiles(input.files)
}

const handleDrop = (e: DragEvent) => {
  if (e.dataTransfer?.files)
    handleFiles(e.dataTransfer.files)
}

const handleFiles = (files: FileList) => {
  const fileArray = Array.from(files)

  fileArray.forEach(file => {
    loading.value.push(true)

    const reader = new FileReader()
    const currentIndex = loading.value.length - 1

    reader.onload = async e => {
      if (e.target?.result) {
        previews.value.push({ id: null, file, url: e.target.result as string })
        await upload(file, currentIndex)
      }
    }
    reader.readAsDataURL(file)
  })
}

const upload = async (file: File, index: number) => {
  try {
    const galeria = await uploadToServer({
      url: file,

      // outros campos se necessário
    })

    // Atualiza o id do preview com o id retornado do backend
    if (galeria && galeria.length) {
      previews.value[index].id = galeria[galeria.length - 1].id
      previews.value[index].url = galeria[galeria.length - 1].url as string
    }
    loading.value[index] = false
  }
  catch (e) {
    previews.value.splice(index, 1)
    loading.value.splice(index, 1)
  }
}

const removeImage = async (id: string, index: number) => {
  removing.value[index] = true
  await deleteGaleria(id)
    .then(() => {
      previews.value.splice(index, 1)
      loading.value.splice(index, 1)
      removing.value.splice(index, 1)
    })
    .finally(() => {
      // Garante que o loading de exclusão seja removido mesmo em caso de erro
      removing.value[index] = false
    })
}

onMounted(() => {
  fetchGaleria()
})
</script>

<template>
  <div
    class="app-text-field flex-grow-1"
    :class="$attrs.class"
  >
    <VCard
      flat
      rounded
    >
      <!-- Dropzone -->
      <div
        class="dropzone"
        @dragover.prevent
        @drop.prevent="handleDrop"
      >
        <label
          for="file-upload"
          class="dropzone-label"
        >
          <VIcon size="48">tabler-upload</VIcon>
          <p><strong>Escolha um arquivo</strong> ou arraste aqui.</p>
        </label>
        <input
          id="file-upload"
          type="file"
          multiple
          accept="image/*"
          class="d-none"
          @change="handleFileChange"
        >
      </div>

      <!-- Múltiplos -->
      <VRow
        v-if="previews.length"
        class="mt-2 mx-0"
        dense
      >
        <VCol
          v-for="(preview, index) in previews"
          :key="index"
          cols="12"
          md="2"
        >
          <VCard>
            <div class="image-wrapper">
              <VImg
                :src="preview.url"
                aspect-ratio="1"
                cover
                eager
              >
                <template #placeholder>
                  <VRow
                    align="center"
                    class="fill-height ma-0"
                    justify="center"
                  >
                    <VProgressCircular
                      color="grey-lighten-5"
                      indeterminate
                    />
                  </VRow>
                </template>
              </VImg>
              <VOverlay
                :model-value="loading[index] || removing[index]"
                class="align-center justify-center"
                contained
              >
                <VProgressCircular
                  color="primary"
                  size="30"
                  indeterminate
                />
              </VOverlay>
              <IconBtn
                class="remove-btn"
                :disabled="removing[index]"
                @click="removeImage(preview.id, index)"
              >
                <VIcon>tabler-x</VIcon>
              </IconBtn>
            </div>
          </VCard>
        </VCol>
      </VRow>
    </VCard>
  </div>
</template>

<style scoped>
.dropzone {
  border: 2px dashed #ccc;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  background-color: #f5f8fa;
  border-radius: 8px;
}
.dropzone-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #607d8b;
}
.d-none {
  display: none;
}
.image-wrapper {
  position: relative;
}
.remove-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 10;
  --v-btn-height: 24px !important;
  background: rgba(var(--v-theme-secondary), 0.8);
  color: white;

  i{
    font-size: 16px !important;
    inline-size: 16px !important;
  }
}
</style>
